import { useNavigate } from "@tanstack/react-router";
import { useI18n } from "../../i18n/I18nProvider";
import { paths } from "../../lib/endpoints";
import { rememberWorkspace } from "../../lib/recent-workspaces";
import { Menu, type MenuItem } from "../ui/Menu";
import { useWorkspaceOptions } from "./WorkspaceSwitcher";

export interface MobileAccountMenuProps {
  workspaceId: string | undefined;
  /** False when the shell could not resolve the current workspace; the list is then left out. */
  showWorkspaces: boolean;
  isAdmin: boolean;
  desktop: boolean;
  onSignOut: () => void;
}

/**
 * Phone header menu: the workspace list, language, admin, desktop download and sign out, all in
 * the one account menu so the header stays a single row.
 */
export function MobileAccountMenu({
  workspaceId,
  showWorkspaces,
  isAdmin,
  desktop,
  onSignOut,
}: MobileAccountMenuProps) {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const workspaces = useWorkspaceOptions(workspaceId);

  const openWorkspace = (id: string) => {
    rememberWorkspace(id);
    void navigate({ to: "/app/$workspaceId/roster", params: { workspaceId: id } });
  };

  const items: MenuItem[] = [
    ...(showWorkspaces
      ? workspaces.map((workspace) => ({
          label: workspace.name,
          checked: workspace.id === workspaceId,
          onSelect: () => openWorkspace(workspace.id),
        }))
      : []),
    { label: t("lang.en"), checked: lang === "en", onSelect: () => setLang("en") },
    { label: t("lang.rs"), checked: lang === "sr", onSelect: () => setLang("sr") },
    ...(isAdmin
      ? [{ label: t("admin.nav"), onSelect: () => void navigate({ to: "/admin" }) }]
      : []),
    ...(desktop
      ? [
          {
            label: t("download.compact"),
            onSelect: () => window.location.assign(paths.desktopDownload),
          },
        ]
      : []),
    { label: t("user.signOut"), onSelect: onSignOut },
  ];

  return <Menu label={t("user.menu")} items={items} />;
}
